import {
  AccordionDetails,
  AccordionSummary,
  Box,
  IconButton,
  styled,
  Tooltip,
  useMediaQuery,
} from '@mui/material';
import { FlexBox } from 'components/Structure';
import { GeoDataBatchRelation, batchDetailSchema } from 'constants/schemas/riskAnalysis.schema';
import { ThemeButton, ThemeTypography } from 'designSystem';
import { Accordion } from 'designSystem/DataDisplay/Accordion/Accordion';
import Icon from 'designSystem/Primitives/Icon/Icon';
import { Field, FieldProps, useField } from 'formik';
import React, { FC, MouseEvent } from 'react';
import { getCountryLabelFromCode } from 'utils/country.utils';
import BatchCardField from './BatchCardField';
import { useDialog } from 'components/hooks';
import { EGeoDataEntity, GEO_DATA_ENTITY_ICON_NAME, IGeoData } from 'types/geodata.types';
import { useTheme } from '@mui/material/styles';

interface IGeoDataAccordionFieldProps extends FieldProps<GeoDataBatchRelation> {
  geoData?: IGeoData;
}

const StyledAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
  '& .MuiAccordionSummary-content': {
    justifyContent: 'space-between',
    paddingRight: theme.spacing(2),
    overflow: 'hidden',
    width: '100%',
  },
}));

const GeoDataAccordionField: FC<IGeoDataAccordionFieldProps> = ({
  field: { name, value },
  geoData,
}) => {
  const [, , { setValue }] = useField<GeoDataBatchRelation>(name);
  const { openDialog } = useDialog();
  const theme = useTheme();
  const mediumScreen = useMediaQuery(theme.breakpoints.down('lg'));

  const selectableHsCodes = value.rawMaterial.hsCodes;

  const handleAddBatch = (event: MouseEvent | KeyboardEvent) => {
    event.stopPropagation();
    setValue({ ...value, batches: [...value.batches, { ...batchDetailSchema.default() }] });
  };

  const handleOpenMap = (event: MouseEvent | KeyboardEvent) => {
    event.stopPropagation();
    if (!geoData) return;

    openDialog({
      type: 'GEO_DATA_PLOT_OVERVIEW',
      props: {
        geoDataId: geoData.id,
      },
    });
  };

  const handleDeleteBatch = (batchIndex: number) => {
    setValue({ ...value, batches: [...value.batches.filter((_, index) => index !== batchIndex)] });
  };

  return (
    <Accordion defaultExpanded elevated>
      <StyledAccordionSummary>
        <FlexBox maxWidth="100%">
          <Icon
            name={GEO_DATA_ENTITY_ICON_NAME[geoData?.dataType || EGeoDataEntity.DATASET]}
            color={geoData?.dataType === EGeoDataEntity.DATASET ? 'gray-100' : 'blue-ice'}
            size="xxx-large"
            mr={2}
          />
          <Box display="flex" flexDirection="column" maxWidth="100%">
            <ThemeTypography variant="ITEM_TITLE" autoOverflow>
              {value.title}
            </ThemeTypography>
            <FlexBox>
              <FlexBox mr={1}>
                <Icon name="raw-material" color="gray-80" size="small" mr={0.5} />
                <ThemeTypography variant="BODY_MEDIUM_BOLD" color="GRAY_80" autoOverflow>
                  {value.rawMaterial.title}
                </ThemeTypography>
              </FlexBox>
              <FlexBox mr={1}>
                <Icon name="location" color="gray-80" size="small" mr={0.5} />
                <ThemeTypography variant="BODY_MEDIUM_BOLD" color="GRAY_80" autoOverflow>
                  {getCountryLabelFromCode(value.originCountry)}
                </ThemeTypography>
              </FlexBox>
              {value.ownedBy.name && (
                <FlexBox>
                  <Icon name="partner" color="gray-80" size="small" mr={0.5} />
                  <ThemeTypography variant="BODY_MEDIUM_BOLD" color="GRAY_80" autoOverflow>
                    {value.ownedBy.name}
                  </ThemeTypography>
                </FlexBox>
              )}
            </FlexBox>
          </Box>
        </FlexBox>
        <FlexBox gap={1} ml={1}>
          {!!geoData && (
            <>
              {mediumScreen ? (
                <Tooltip title="View plots on map">
                  <IconButton onClick={handleOpenMap}>
                    <Icon name="map" />
                  </IconButton>
                </Tooltip>
              ) : (
                <ThemeButton
                  color="BLUE_ICE"
                  startIcon={<Icon name="map" />}
                  onClick={handleOpenMap}
                >
                  View plots on map
                </ThemeButton>
              )}
            </>
          )}
          {mediumScreen ? (
            <Tooltip title="Add origin batch">
              <IconButton onClick={handleAddBatch}>
                <Icon name="plus" />
              </IconButton>
            </Tooltip>
          ) : (
            <ThemeButton color="BLUE_ICE" startIcon={<Icon name="plus" />} onClick={handleAddBatch}>
              Add origin batch
            </ThemeButton>
          )}
        </FlexBox>
      </StyledAccordionSummary>
      <AccordionDetails>
        <Box display="flex" gap={1} flexDirection="column" flexWrap="wrap">
          {value.batches.map((_, batchIndex) => (
            <Field
              key={`${name}.batches.[${batchIndex}]`}
              name={`${name}.batches.[${batchIndex}]`}
              enableDelete={value.batches.length > 1}
              hsCodes={selectableHsCodes}
              component={BatchCardField}
              onBatchDelete={() => handleDeleteBatch(batchIndex)}
            />
          ))}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export default GeoDataAccordionField;
